import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useI18n } from "../i18n";
import { useAuth } from "../AuthContext";

export default function Nav() {
  const { t, lang, setLang } = useI18n();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);

  // Lock body scroll while the mobile drawer is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  return (
    <header
      className="sticky top-0 z-50 bg-white"
      style={{ borderBottom: "1px solid var(--line)" }}
      data-testid="main-nav"
    >
      <div className="container-cd flex items-center justify-between" style={{ height: 72 }}>
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
          <div
            className="w-9 h-9 flex items-center justify-center font-display text-white"
            style={{ background: "var(--ink)" }}
          >
            CD
          </div>
          <div className="leading-tight">
            <div className="font-display text-base">CORRIDOR DIGITAL</div>
            <div className="overline" style={{ fontSize: 9 }}>CHIHUAHUA ↔ EL PASO</div>
          </div>
        </Link>

        {/* Desktop nav — unchanged */}
        <nav className="hidden md:flex items-center gap-8 font-heading text-sm">
          <Link to="/directory" className="hover:text-[color:var(--orange)]" data-testid="nav-directory">
            {t("nav.directory")}
          </Link>
          <Link to="/plants" className="hover:text-[color:var(--orange)]" data-testid="nav-plants">
            {t("nav.plants")}
          </Link>
          <Link to="/digest" className="hover:text-[color:var(--orange)]" data-testid="nav-digest">
            {t("nav.digest")}
          </Link>
          <Link to="/pricing" className="hover:text-[color:var(--orange)]" data-testid="nav-pricing">
            {t("nav.pricing")}
          </Link>
          <Link to="/founding" className="hover:text-[color:var(--orange)] font-semibold" style={{ color: "var(--orange)" }} data-testid="nav-founding">
            ✦ Founding 10
          </Link>
          {user ? (
            <Link to="/dashboard" className="hover:text-[color:var(--orange)]" data-testid="nav-dashboard">
              {t("nav.dashboard")}
            </Link>
          ) : null}
        </nav>

        {/* Desktop right-side controls — unchanged */}
        <div className="hidden md:flex items-center gap-3">
          <div className="flex border border-black" data-testid="lang-toggle">
            <button
              onClick={() => setLang("es")}
              className="px-2.5 py-1 font-mono text-xs"
              style={{
                background: lang === "es" ? "var(--ink)" : "transparent",
                color: lang === "es" ? "white" : "var(--ink)",
              }}
              data-testid="lang-es"
            >
              ES
            </button>
            <button
              onClick={() => setLang("en")}
              className="px-2.5 py-1 font-mono text-xs"
              style={{
                background: lang === "en" ? "var(--ink)" : "transparent",
                color: lang === "en" ? "white" : "var(--ink)",
              }}
              data-testid="lang-en"
            >
              EN
            </button>
          </div>

          {user ? (
            <button
              className="btn btn-ghost btn-sm"
              onClick={async () => { await logout(); navigate("/"); }}
              data-testid="nav-logout"
            >
              {t("nav.logout")}
            </button>
          ) : (
            <>
              <Link
                to={`/login?next=${encodeURIComponent(location.pathname)}`}
                className="btn btn-ghost btn-sm"
                data-testid="nav-login"
              >
                {t("nav.login")}
              </Link>
              <Link to="/register" className="btn btn-primary btn-sm" data-testid="nav-signup">
                {t("nav.signup")}
              </Link>
            </>
          )}
        </div>

        {/* Mobile-only hamburger trigger */}
        <button
          type="button"
          className="md:hidden flex items-center justify-center"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((v) => !v)}
          style={{
            width: 44,
            height: 44,
            border: "1px solid var(--ink)",
            background: menuOpen ? "var(--ink)" : "white",
            color: menuOpen ? "white" : "var(--ink)",
          }}
          data-testid="nav-mobile-toggle"
        >
          {menuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Mobile drawer — full-width below the header */}
      {menuOpen && (
        <div
          className="md:hidden"
          style={{
            position: "fixed",
            top: 72,
            left: 0,
            right: 0,
            bottom: 0,
            background: "white",
            zIndex: 49,
            overflowY: "auto",
            borderTop: "1px solid var(--line)",
          }}
          data-testid="nav-mobile-drawer"
        >
          <nav className="container-cd py-6 flex flex-col gap-1 font-heading">
            <MobileLink to="/directory" testid="nav-m-directory" onClick={closeMenu}>{t("nav.directory")}</MobileLink>
            <MobileLink to="/plants" testid="nav-m-plants" onClick={closeMenu}>{t("nav.plants")}</MobileLink>
            <MobileLink to="/digest" testid="nav-m-digest" onClick={closeMenu}>{t("nav.digest")}</MobileLink>
            <MobileLink to="/pricing" testid="nav-m-pricing" onClick={closeMenu}>{t("nav.pricing")}</MobileLink>
            <MobileLink to="/founding" testid="nav-m-founding" onClick={closeMenu} highlight>
              ✦ Founding 10
            </MobileLink>
            {user ? (
              <MobileLink to="/dashboard" testid="nav-m-dashboard" onClick={closeMenu}>{t("nav.dashboard")}</MobileLink>
            ) : null}

            <div style={{ height: 1, background: "var(--line)", margin: "16px 0" }} />

            {/* Language toggle */}
            <div className="flex items-center gap-3 mb-3">
              <span className="overline">LANG</span>
              <div className="flex border border-black" data-testid="lang-toggle-m">
                <button
                  onClick={() => setLang("es")}
                  className="px-3 py-2 font-mono text-xs"
                  style={{
                    background: lang === "es" ? "var(--ink)" : "transparent",
                    color: lang === "es" ? "white" : "var(--ink)",
                    minHeight: 36,
                  }}
                  data-testid="lang-es-m"
                >
                  ES
                </button>
                <button
                  onClick={() => setLang("en")}
                  className="px-3 py-2 font-mono text-xs"
                  style={{
                    background: lang === "en" ? "var(--ink)" : "transparent",
                    color: lang === "en" ? "white" : "var(--ink)",
                    minHeight: 36,
                  }}
                  data-testid="lang-en-m"
                >
                  EN
                </button>
              </div>
            </div>

            {/* Auth actions */}
            {user ? (
              <button
                className="btn btn-ghost btn-lg w-full"
                onClick={async () => { await logout(); setMenuOpen(false); navigate("/"); }}
                style={{ minHeight: 48 }}
                data-testid="nav-m-logout"
              >
                {t("nav.logout")}
              </button>
            ) : (
              <div className="flex flex-col gap-2">
                <Link
                  to={`/login?next=${encodeURIComponent(location.pathname)}`}
                  className="btn btn-ghost btn-lg w-full"
                  style={{ minHeight: 48 }}
                  data-testid="nav-m-login"
                  onClick={closeMenu}
                >
                  {t("nav.login")}
                </Link>
                <Link
                  to="/register"
                  className="btn btn-primary btn-lg w-full"
                  style={{ minHeight: 48 }}
                  data-testid="nav-m-signup"
                  onClick={closeMenu}
                >
                  {t("nav.signup")}
                </Link>
              </div>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}

function MobileLink({ to, children, testid, highlight, onClick }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="flex items-center justify-between"
      style={{
        padding: "14px 0",
        minHeight: 48,
        fontSize: 18,
        fontWeight: highlight ? 600 : 500,
        color: highlight ? "var(--orange)" : "var(--ink)",
        borderBottom: "1px solid var(--line)",
      }}
      data-testid={testid}
    >
      <span>{children}</span>
      <span style={{ color: "var(--ink-2)", fontSize: 14 }}>→</span>
    </Link>
  );
}
